<template>
  <div>
    <div v-if="setIsLoading" class="row mt-4">

      <div class="col-md-12 col-12 form-field">

        <p>
          You can save this form as a draft at any point after this first screen if needed. <br />
          After you submit the form, you will be directed to the appropriate Autodesk purchasing or contracting process to complete your transaction.<br/>
          Be sure to review the tooltips throughout the engagement form for recommendations and help on how to complete the form more effectively. If you have any questions, please ask <a :href="CONSTANTS.SUPPLIER_CENTRAL_SLACK_URL" target="_blank">Lumi</a> on Slack.
        </p>

        <div class="title pt-2">Requested by</div>
        <div class="mb-4">
          <input class="form-control w-100 mt-1" v-model="supplierOnboardRequestData.requestedByFullName" disabled="true" />
        </div>

        <p>
          <input type="checkbox" @click="toggleRequestingForOthers" :checked="isRequestingForOthers">
          <span class="title ml-2" @click="toggleRequestingForOthers">I am submitting this request on behalf of someone else</span>
        </p>

        <div v-if="isRequestingForOthers">
          <div class="title">
            <div class="required-field">Requested for</div>
            <div class="text-muted">This person will be responsible for answering detailed supplier questions.</div>
          </div>

          <div class="mb-4 mt-2">

            <help-hub-user-search
                :env="appEnv"
                @onUserSelected="onRequestedForUserSelected"
                @onUserRemoved="clearRequestedForUser"
                autocomplete="nope"
                :selected-users="requestedForUser"
                ref="userSearch"
            />
            <span
                class="text-danger input-error-text"
                v-if="userSearchIsInvalid"
            >Requested for field is mandatory</span>
          </div>
        </div>

        <p>
          <input type="checkbox" @click="toggleRequestToAddWatchers" :checked="isRequestingForWatchers">
          <span class="title ml-2" @click="toggleRequestToAddWatchers">I wish to allow watchers on this request</span>
        </p>


        <div v-if="isRequestingForWatchers">
          <div class="title">
            <div class="required-field">Request to add Watcher</div>
            <div class="text-muted">The watchers will be able to track your request on your behalf.</div>
          </div>

          <div class="mb-4 mt-2">
            <help-hub-user-search
                :env="appEnv"
                :is-multi-select="true"
                :selected-users="stringifiedWatchers"
                @onUserSelected="onWatchersSelected"
                @onUserRemoved="onUserRemoved">
            </help-hub-user-search>
            <span
                class="text-danger input-error-text"
                v-if="isWatchersEmpty"
            >Request to add Watcher field is mandatory</span>
          </div>
        </div>


        <div class="title required-field" :class="[!isRequestingForOthers ? 'mt-4':'']">
          <span>1. </span>
          <span>Detailed description of product or service the supplier will be providing</span>
        </div>
        <div class="mb-4">
          <textarea
              class="form-control mt-1 text-area-resize-none"
              v-validate="'required'"
              name="supplierOnboardRequestData.productDescription"
              v-model="supplierOnboardRequestData.productDescription"
              data-vv-validate-on="input"
          />
          <span
              class="text-danger input-error-text"
              v-if="errors.has('supplierOnboardRequestData.productDescription')"
          >Detailed description field is required</span>
        </div>


        <div class="title pt-2">
          <span>2. </span>
          <span>
            Product name <br>
            <span class="font-italic">Only complete #2 if you are purchasing a product. For professional services, leave blank.</span>
          </span>
        </div>
        <div class="mb-4">
          <input
              class="form-control w-100 mt-1"
              name="supplierOnboardRequestData.productName"
              v-model="supplierOnboardRequestData.productName"
              id="supplierOnboardRequestData.productName"
              autocomplete="off"
              @blur="checkIfProductExists"
          >
        </div>

        <div v-if="validProductAlreadyExists && !isRedirectedFromSoftwarePage" class="preview-notification">
          Product {{productFound.name}} is already available, you can
          <a :href="productFound.url" target="_blank" class="solution-link" rel="noopener noreferrer">get it</a> here
        </div>

        <div v-if="validProductAlreadyExists && isRedirectedFromSoftwarePage" class="preview-notification">
          Product {{productFound.name}} is already available, If you do not plan to share personal data, you can
          <a :href="productFound.url" target="_blank" class="solution-link" rel="noopener noreferrer">get it</a> here
        </div>



        <div class="title pt-2 required-field">
          <span>3. </span>
          <header-with-tooltip
              :tooltip-text="`Standard requests may take 5 to 10  business days to complete. Please allow longer if additional reviews are necessary.`"
              :isHTML="true"
              :show-icon="true">
            <span class="mr-1">Requested services start date</span>
          </header-with-tooltip>
        </div>
        <div class="mb-4 mt-1">
          <date-picker-widget
              v-model="supplierOnboardRequestData.requestedStartDate"
              class="col-xl-3 col-12 px-0"
              name="supplierOnboardRequestData.requestedStartDate"
              v-validate="'required'"
              data-vv-validate-on="input"
          ></date-picker-widget>
          <span
              class="text-danger input-error-text"
              v-if="errors.has('supplierOnboardRequestData.requestedStartDate')"
          >Start date field is required</span>
        </div>


        <div class="pb-2">
          <div class="title mt-3 required-field">
            <span>4. </span>
            <span>Procurement category (determines routing within Procurement)</span>
          </div>
          <div class="my-2">
            <div class="item select-style">
              <model-list-select
                  :list="supplierServiceCategoryList"
                  :optionValue="'value'"
                  :optionText="'text'"
                  name="supplierOnboardRequestData.category"
                  v-validate="'required'"
                  data-vv-validate-on="input"
                  v-model="supplierOnboardRequestData.category"
                  @input="validateCategoryOption($event)"
                  class="w-50"
                  :isDisabled="supplierOnboardRequestData.duplicateForm"
              />
            </div>
            <span
                class="text-danger input-error-text"
                v-if="errors.has('supplierOnboardRequestData.category')"
            >Procurement category field is required</span>
          </div>
        </div>

        <div class="title pt-2 required-field">
          <span>5. </span>
          <span>Business justification to engage with supplier</span>
        </div>
        <div class="mb-4">
          <textarea
              class="form-control w-100 mt-1 text-area-resize-none"
              v-validate="'required'"
              data-vv-validate-on="input"
              name="supplierOnboardRequestData.businessJustification"
              v-model="supplierOnboardRequestData.businessJustification"
          />
          <span
              class="text-danger input-error-text"
              v-if="errors.has('supplierOnboardRequestData.businessJustification')"
          >Business justification field is required</span>
        </div>

          <div class="mb-1">
            <div class="title required-field">
              <span>6. Does the supplier have fewer than 5 full time, regular employees?</span>
            </div>
            <div>
              <span class="font-italic notification-text"> Note: Answering this question incorrectly could result in the purchase not following correct procedures and delay completion or require re-submission. Ask us on <a :href="CONSTANTS.LUMI_SLACK" target="_blank">Lumi</a> if you have a question.</span>
            </div>


            <div class="d-inline" :key="index" v-for="(radio,index) in CONSTANTS.ONE_INTAKE_RADIO_BOOLEAN_EMPLOYEE">
              <label class="row no-gutters ml-3 mt-2">
                <input
                    class="vertical-radio"
                    :key="index"
                    type="radio"
                    name="supplierOnboardRequestData.supplierEmployeeCountBelowFive"
                    v-model="supplierOnboardRequestData.supplierEmployeeCountBelowFive"
                    :value="radio.value"
                    v-validate="'required'"
                    :disabled="supplierOnboardRequestData.duplicateForm"
                > {{radio.text}}</label>
            </div>
            <div
                class="text-danger input-error-text"
                v-if="errors.has('supplierOnboardRequestData.supplierEmployeeCountBelowFive')"
            >This field is required</div>
          </div>

          <div class="pb-2 border-container" v-show="isSupplierEmployeeCountBelowFive && !isProcurementCategoryICException">
            <div class="title mt-3 required-field">
              <span>6a. </span>
              <header-with-tooltip
                  :tooltip-text="supplierServiceHelpText"
                  :isHTML="true"
                  :show-icon="true">
                <span class="mr-1">Which service is your supplier providing?</span>
              </header-with-tooltip>
            </div>
            <div class="my-2">
              <div class="item select-style">
                <model-list-select
                    :list="supplierServiceOptions"
                    :optionValue="'value'"
                    :optionText="'text'"
                    name="supplierOnboardRequestData.service"
                    v-validate="isSupplierEmployeeCountBelowFive && !isProcurementCategoryICException ? 'required' : ''"
                    data-vv-validate-on="input"
                    v-model="supplierOnboardRequestData.service"
                    class="w-50"
                    :isDisabled="supplierOnboardRequestData.duplicateForm"
                />
              </div>
              <span
                  class="text-danger input-error-text"
                  v-if="errors.has('supplierOnboardRequestData.service')"
              >This field is required</span>
            </div>

          <div v-show="!isSupplierServiceICException && !isProcurementCategoryICException">
            <div class="title required-field mt-2 mb-3">
              <span>6b. </span>
              <span>Where is your Supplier located?</span>
            </div>
            <div class="d-inline" :key="index" v-for="(radio,index) in CONSTANTS.ONE_INTAKE_SUPPLIER_LOCATED_RADIO_OPTIONS">
              <label class="row no-gutters ml-3">
                <input
                    class="vertical-radio"
                    :key="index"
                    type="radio"
                    name="supplierOnboardRequestData.supplierLocated"
                    v-model="supplierOnboardRequestData.supplierLocated"
                    :value="radio.text"
                    v-validate="!isSupplierServiceICException && !isProcurementCategoryICException ? 'required' : ''"
                    :disabled="supplierOnboardRequestData.duplicateForm"
                > {{radio.text}}</label>
            </div>
            <div
                class="text-danger input-error-text"
                v-if="errors.has('supplierOnboardRequestData.supplierLocated')"
            >This field is required</div>
          </div>
        </div>

        <div class="mb-3">
          <div class="title required-field pt-2">
            <span>7. </span>
            <header-with-tooltip
                :tooltip-text="autodeskBuyingServices"
                :isHTML="true"
                :show-icon="true">
              <span class="mr-1">What Autodesk company is buying these services / this product?</span>
            </header-with-tooltip>
          </div>
          <div class="item select-style custom-input">

            <div class="d-inline" :key="index" v-for="(checkbox,index) in oneIntakeCheckBoxCountries">
              <label class="row no-gutters mt-2 mb-2 ml-3">
                <input
                    class="oneIntakeCheckBoxCountries"
                    :key="index"
                    type="checkbox"
                    name="supplierOnboardRequestData.companyBuyingServiceCheckBox"
                    v-model="supplierOnboardRequestData.companyBuyingServiceCheckBox"
                    :value="checkbox"
                    data-vv-validate-on="input"
                > {{checkbox.text}}</label>
            </div>

            <input type="checkbox" id="other" @click="toggleOthersSelected" :checked="isCompanyBuyingServicesOthersSelected" class="oneIntakeCheckBoxCountries ml-3">
            <label for="other">Other</label>

            <multi-select
              v-if="isCompanyBuyingServicesOthersSelected"
              :options="countryListOptions"
              :optionText="countryListOptions.text"
              :optionValue="countryListOptions.value"
              :selected-options="supplierOnboardRequestData.companyBuyingServices"
              data-vv-validate-on="input"
              name="supplierOnboardRequestData.companyBuyingServices"
              @select="onSelect"
              v-model="supplierOnboardRequestData.companyBuyingServices"
            />

            <span class="text-danger d-block input-error-text"
                  v-if="isCompanyBuyingServicesEmpty"
            >This field is required</span>
          </div>
        </div>

        <div class="pt-2">
          <attach-invoice :data="supplierOnboardRequestData"
                          @onInvoiceUploaded="onDocUploaded"
                          @onInvoiceDeleted="onDocDeleted"
                          :is-required="false"
                          :viewAttachment="false"
                          :attachment-title="CONSTANTS.ATTACH_A_SUPPORTING_DOC"
                          :max-file-size-in-mb="CONSTANTS.ATTACHMENTS_MAX_SIZE_IN_MB.ONE_INTAKE_FORM"
                          :validFileTypes="CONSTANTS.FILE_TYPES.extensionAsAll.validFileTypes"
                          :multipleSelectionAllowed="false"
                          :check-for-file-support="false"
          />
        </div>
        <div class="float-md-right mt-4 pr-0">
          <button class="btn-container flat-btn gray-btn close-btn btn mx-2 my-3" @click="navigateToHome()">Cancel</button>
          <button class="btn-container btn flat-btn primary-blue-btn ml-2 my-3" @click="validateAndSetCurrentTab()">Next</button>
        </div>

      </div>
    </div>
    <loading-spinner v-else />
  </div>
</template>

<script>
import { mapMutations,mapGetters, mapActions } from "vuex";
import HeaderWithTooltip from "../HeaderWithTooltip";
import supplierRequestMixin from "@/mixins/supplierRequestMixin";
import CONSTANTS from "@/store/constants";
import filter from "@/filters/filter";
import {ModelListSelect,MultiSelect} from "vue-search-select";
import DatePickerWidget from "@/components/DatePicker";
import AttachInvoice from "../Payments/AttachInvoice";
import swal from "sweetalert2";
import LoadingSpinner from "@/components/LoadingSpinner.vue";

export default {
  name: "GeneralInformation",
  components: {LoadingSpinner, DatePickerWidget, ModelListSelect,MultiSelect,AttachInvoice,HeaderWithTooltip},
  mixins: [supplierRequestMixin],
  data() {
    return {
      appEnv: CONSTANTS.ENV,
      requestedForUser: {},
      productFound: {},
      userSearchIsInvalid: false,
      isWatchersEmpty: false,
      questionNumber: 1,
      isCompanyBuyingServicesEmpty: false,
      autodeskBuyingServices: `If you do not know, refer to the first 4 numbers of your cost center. Select all companies related to your engagement. The Autodesk company choosen should be the company that will benefitiate from the services (i.e Global project = Autodesk Inc, EMEA = Autodesk Ireland, local = the country).`,
      fewerThanSixRegularEmployees: `Answering this question incorrectly could result in the purchase not following correct procedures and delay completion or require re-submission. Ask <a href=${CONSTANTS.LUMI_SLACK} target="_blank">LUMI</a> on Slack if you have a question.`,
      isRedirectedFromSoftwarePage: false,
      supplierServiceHelpText: `Selecting the type of services being provided by the supplier will determine the contracting requirements and next steps.`

    }
  },
  watch: {
    isCompanyBuyingServicesOthersSelected(val){
      if (!val){
        this.supplierOnboardRequestData.companyBuyingServices = []
        this.isCompanyBuyingServicesEmpty = false
      }
    },
    isRequestingForOthers(val) {
      if(!val) {
        this.clearRequestedForUser()
      }
    },
    isSupplierEmployeeCountBelowFive(val){
      if(!val) {
        this.$set(this.$store.state.supplierOnboardRequestData, 'supplierLocated',undefined);
        this.$set(this.$store.state.supplierOnboardRequestData, 'service',null);
      }
    },
    isSupplierServiceICException(val){
      if(val){
        this.$set(this.$store.state.supplierOnboardRequestData, 'supplierLocated',undefined);
      }
    },
    isProcurementCategoryTechnology(val){
      if(!val) {
        this.$set(this.$store.state.supplierOnboardRequestData, 'technologyFromReseller',null);
        this.$set(this.$store.state.supplierOnboardRequestData, 'integrateWithADSKNetwork',null);
        this.$set(this.$store.state.supplierOnboardRequestData, 'cookieInstalledOnADSKNetwork',null);
        this.$set(this.$store.state.supplierOnboardRequestData, 'artificialIntelligenceOrMachineLearning',null);
        this.$set(this.$store.state.supplierOnboardRequestData, 'customerIntellectualProperty',null);
        this.$set(this.$store.state.supplierOnboardRequestData, 'softwareInstalledOnADSKMachine',null);
      }
    },
    isProcurementCategoryICException(val){
      if(val){
        this.$set(this.$store.state.supplierOnboardRequestData, 'supplierLocated',undefined);
        this.$set(this.$store.state.supplierOnboardRequestData, 'service',null);
      }
    },
    adminDataList(val) {
      if (val) {
        this.initializeGeneralInformation();
      }
    },
    isICRequest(val){
      if(val){
        this.updateCompanyBuyingServices();
        this.$set(this.$store.state.supplierOnboardRequestData, 'technologyFromReseller',null);
        this.$set(this.$store.state.supplierOnboardRequestData, 'cookieInstalledOnADSKNetwork',null);
        this.$set(this.$store.state.supplierOnboardRequestData, 'softwareInstalledOnADSKMachine',null);
        this.$set(this.$store.state.supplierOnboardRequestData, 'isPilotOrProofOfConcept',null);
      }else{
        this.restoreCompanyBuyingServices();
      }
      if(!val && !this.isAcquiringThirdPartyTech){
        this.$set(this.$store.state.supplierOnboardRequestData, 'integrateWithADSKNetwork',null);
      }
    },
    "supplierOnboardRequestData.supplierLocated"(newValue, oldValue) {
      const isCountrySelected = this.supplierOnboardRequestData.companyBuyingServiceCheckBox.some(val => val.country === newValue);

      if (!isCountrySelected) {
        const countryToAdd = this.oneIntakeCheckBoxCountries.find(val => val.country === newValue);

        if (countryToAdd) {
          this.supplierOnboardRequestData.companyBuyingServiceCheckBox.push(countryToAdd);
        }
      }
      this.supplierOnboardRequestData.companyBuyingServiceCheckBox = this.supplierOnboardRequestData.companyBuyingServiceCheckBox.filter(val => val.country !== oldValue);
    }
  },
  created: function() {
    if (this.adminDataListExists()) {
      this.initializeGeneralInformation();
    }
  },
  computed: {
    ...mapGetters([
      "supplierOnboardRequestData",
      "isRequestingForOthers",
      "products",
      "supplierServiceCategoryOptions",
      "supplierServiceOptions",
      "watchers",
      "isRequestingForWatchers",
      "currentUser",
      "returnedRequestId",
      "draftId",
      "isCompanyBuyingServicesOthersSelected",
      "requesterCountryBuyingService"
    ]),
    validProductAlreadyExists() {
      return Object.keys(this.productFound).length !== 0 && this.productFound.constructor === Object
    },
    stringifiedWatchers() {
      if (!this.watchers) return undefined;
      return JSON.stringify(this.watchers);
    },
    isSupplierEmployeeCountBelowFive(){
      return this.supplierOnboardRequestData.supplierEmployeeCountBelowFive === true
    },
    isSupplierServiceICException(){
      return this.supplierOnboardRequestData.service !== "None of the above";
    },
    supplierServiceCategoryList(){
      return [...this.supplierServiceCategoryOptions].sort((a,b)=> a.value.localeCompare(b.value))
    },
    isProcurementCategoryICException(){
      return this.supplierOnboardRequestData.category === "Facilities" || this.supplierOnboardRequestData.category === "Travel & Events";
    }
  },
  methods: {
    ...mapMutations(["setCurrentTab",'setRequestedFor','setIsRequestingForOthers','setAdskContactInfo','setWatchers','setIsRequestingForWatchers','setIsCompanyBuyingServicesOthersSelected','setRequesterCountryBuyingService']),
    ...mapActions(['fetchAdskContactInfo']),
    setThirdPartyLicenseRequired(index) {
      if (index === CONSTANTS.PROCUREMENT_TECHNOLOGY_OPTION && !this.draftId && !this.returnedRequestId){
        this.$set(this.$store.state.supplierOnboardRequestData, 'thirdPartyLicenseRequired',true);
      }else {
        this.$set(this.$store.state.supplierOnboardRequestData, 'thirdPartyLicenseRequired',undefined);
      }
    },
    validateCategoryOption(index){
      if (Array.isArray(CONSTANTS.SUPPLIER_CENTRAL_CATEGORIES_EXCEPTIONS_LIST) && (CONSTANTS.SUPPLIER_CENTRAL_CATEGORIES_EXCEPTIONS_LIST).includes(index)){
        swal
        .fire({
          allowEscapeKey: true,
          showConfirmButton: true,
          showCancelButton: true,
          confirmButtonText: "Re-Direct",
          cancelButtonText: "Go-Back",
          customClass: {
          confirmButton: "flat-btn primary-blue-btn btn mb-1",
          cancelButton: "flat-btn gray-btn btn mb-1 swalSubmit"
          },
          html: `<h4><b>This category does not require a supplier central ticket</b></h4> Instead, please click on Re-Direct to submit your request as a Supplier Central Exception or click on Go-Back to go to previous page.`,
        })
        .then((result) => { 
          if (result.isConfirmed) {
            CONSTANTS.ONE_INTAKE_FORM_REDIRECT_SELECTION = this.getTextValue(this.supplierServiceCategoryOptions, index);
            this.$router.replace("/exception-submission");
          }
          else{
            this.$set(this.$store.state.supplierOnboardRequestData, 'category',undefined);
          }
        });
      }
      else{
        return this.setThirdPartyLicenseRequired(index);
      }
    },
    initializeGeneralInformation(){
      /* the below conditions are added to specifically handle the case for the case when in returned requests wherein if requestedFor === requestedBy,
    the requestedFor checkbox toggles to true and requestedFor is set to same as requestedBy.
     */
      if(this.$route.params.productName && this.$route.params.productName !== ''){
        this.supplierOnboardRequestData.productName = this.$route.params.productName
        this.isRedirectedFromSoftwarePage = true
      }
      if (this.supplierOnboardRequestData.requestedFor === this.supplierOnboardRequestData.requestedBy){
        this.setIsRequestingForOthers(false);
      }
      if(this.supplierOnboardRequestData && this.supplierOnboardRequestData.requestedFor && this.supplierOnboardRequestData.requestedFor !== this.supplierOnboardRequestData.requestedBy) {
        this.setRequestedForUserName()
      }
      if(this.supplierOnboardRequestData && this.supplierOnboardRequestData.productName !== '') {
        this.checkIfProductExists()
      }
      this.updateDataForCountries()
    },
    onSelect(companyBuyingServices) {
      this.supplierOnboardRequestData.companyBuyingServices = companyBuyingServices;
      this.isCompanyBuyingServicesEmpty = false
    },
    updateDataForCountries() {
      if(this.supplierOnboardRequestData
          && this.supplierOnboardRequestData.companyBuyingServices
          //The check here for length > 0 also takes care of the case that if companyBuyingServices = ""; we then only enter the if block.
          && this.supplierOnboardRequestData.companyBuyingServices.length > 0 && typeof this.supplierOnboardRequestData.companyBuyingServices === 'string') {
        let companyBuyingServicesList =  this.supplierOnboardRequestData.companyBuyingServices.split(',');
        let companyBuyingListObj = []
        let companyBuyingCheckboxListObj = []
        companyBuyingServicesList.forEach((location) => {
          let oneIntakeCountry = this.countryListOptions.find(country => country.text === location)
          let oneIntakeCountryCheckBox = this.oneIntakeCheckBoxCountries.find(country => country.text === location)

          if(oneIntakeCountry && Object.keys(oneIntakeCountry).length !== 0) {
            companyBuyingListObj.push(oneIntakeCountry)
          } else if(oneIntakeCountryCheckBox && Object.keys(oneIntakeCountryCheckBox).length !== 0) {
            companyBuyingCheckboxListObj.push(oneIntakeCountryCheckBox)
          }
        })

        this.setIsCompanyBuyingServicesOthersSelected(companyBuyingListObj && companyBuyingListObj.length > 0);

        this.supplierOnboardRequestData.companyBuyingServices = companyBuyingListObj
        this.supplierOnboardRequestData.companyBuyingServiceCheckBox = companyBuyingCheckboxListObj
      }else if (Array.isArray(this.supplierOnboardRequestData.companyBuyingServices) && this.supplierOnboardRequestData.companyBuyingServices.length === 0 && this.supplierOnboardRequestData.companyBuyingServiceCheckBox.length === 0) {
        //The elseIf case here is responsible for the scenario when the GeneralInformation Page is freshly loaded/if companyBuyingServices && companyBuyingServiceCheckBox is Empty while giving the
        //user the ability to change the country/add countries as per requirements.
        this.updateCompanyBuyingServices(this.currentUser);
      }
    },
    onWatchersSelected(response){
      let existingWatchers = this.watchers;
      existingWatchers.push(response.detail[0]);
      this.setWatchers(existingWatchers)
      this.isWatchersEmpty = false;
    },
    onUserRemoved(response){
      let existingWatchers = this.watchers;
      if(response.detail.length) {
        const watchersList = existingWatchers.filter(t=>t.userName !== response.detail[0]);
        this.setWatchers(watchersList);
      }
    },
    onDocUploaded(attachments){
      this.showAttachmentsError = false
      this.supplierOnboardRequestData.attachments = attachments;
    },
    onDocDeleted(removeAttachment){
      var existingAttachments = this.supplierOnboardRequestData.attachments;
      this.supplierOnboardRequestData.attachments = existingAttachments.filter( existingAttachment => existingAttachment.fileId !== removeAttachment.fileId);
      this.formAttachmentsExist() ? this.showAttachmentsError = false : this.showAttachmentsError = true
    },
    formAttachmentsExist() {
      return this.supplierOnboardRequestData.attachments && this.supplierOnboardRequestData.attachments.length > 0
    },
    onRequestedForUserSelected(user) {
      this.setRequestedFor({requestedFor: user.detail[0].id, requestedForFullName: user.detail[0].fullName})
      this.setAdskContactInfo(user.detail[0])
      this.userSearchIsInvalid = false
      this.updateCompanyBuyingServices(user.detail[0])
    },
    updateCompanyBuyingServices(userDetails) {
      if(userDetails){
        //This method extracts out the 1st four digits from the costCenter that we receive from the API, matches it with the constants we maintain and auto-populates the field with required data.
        let userCostCenter =  userDetails.costCenter.substring(0,4);
        let companyBuyingService = this.countryListOptions.find(companyObj => companyObj.costCenter === userCostCenter)
        let companyBuyingListObj = []
        let companyBuyingListObjCheckbox = []
        let requesterCountryBuyingService = []
        if(companyBuyingService && Object.keys(companyBuyingService).length !== 0) {
          companyBuyingListObj.push(companyBuyingService)
          requesterCountryBuyingService.push(companyBuyingService)
        } else {
          let companyBuyingServiceCheckbox = this.oneIntakeCheckBoxCountries.find(companyObj => companyObj.costCenter === userCostCenter)
          if(companyBuyingServiceCheckbox && Object.keys(companyBuyingServiceCheckbox).length !== 0) {
            companyBuyingListObjCheckbox.push(companyBuyingServiceCheckbox)
            requesterCountryBuyingService.push(companyBuyingServiceCheckbox)
          }
        }
        this.setRequesterCountryBuyingService(requesterCountryBuyingService)
        if(!this.isICRequest){
          this.setIsCompanyBuyingServicesOthersSelected(companyBuyingListObj && companyBuyingListObj.length > 0);

          this.supplierOnboardRequestData.companyBuyingServices = companyBuyingListObj
          this.supplierOnboardRequestData.companyBuyingServiceCheckBox = companyBuyingListObjCheckbox
        }
      }else{
        this.supplierOnboardRequestData.companyBuyingServiceCheckBox = this.supplierOnboardRequestData.companyBuyingServiceCheckBox.filter(val => this.requesterCountryBuyingService.some((requesterCountry) => val.costCenter !== requesterCountry.costCenter));
        this.supplierOnboardRequestData.companyBuyingServices = this.supplierOnboardRequestData.companyBuyingServices.filter(val => this.requesterCountryBuyingService.some((requesterCountry) => val.costCenter !== requesterCountry.costCenter));
        this.setIsCompanyBuyingServicesOthersSelected(this.supplierOnboardRequestData.companyBuyingServices && this.supplierOnboardRequestData.companyBuyingServices.length > 0);
      }
    },
    restoreCompanyBuyingServices(){
      let companyBuyingService = this.countryListOptions.find(companyObj => this.requesterCountryBuyingService.some((requesterCountry) => companyObj.costCenter === requesterCountry.costCenter))
      if(companyBuyingService && Object.keys(companyBuyingService).length !== 0) {
        this.supplierOnboardRequestData.companyBuyingServices.some(companyObj => companyObj.costCenter === companyBuyingService.costCenter) || this.supplierOnboardRequestData.companyBuyingServices.push(companyBuyingService);
      } else {
        let companyBuyingServiceCheckbox = this.oneIntakeCheckBoxCountries.find(companyObj => this.requesterCountryBuyingService.some((requesterCountry) => companyObj.costCenter === requesterCountry.costCenter))
        if(companyBuyingServiceCheckbox && Object.keys(companyBuyingServiceCheckbox).length !== 0) {
          this.supplierOnboardRequestData.companyBuyingServiceCheckBox.some(companyObj => companyObj.costCenter === companyBuyingServiceCheckbox.costCenter) || this.supplierOnboardRequestData.companyBuyingServiceCheckBox.push(companyBuyingServiceCheckbox);
        }
      }
      this.setIsCompanyBuyingServicesOthersSelected(this.supplierOnboardRequestData.companyBuyingServices && this.supplierOnboardRequestData.companyBuyingServices.length > 0);
    },
    checkIfProductExists() {
      this.productFound = {}
      if(this.products && this.products.length > 0) {
        let foundProduct = this.products.find(product => product.name === this.supplierOnboardRequestData.productName)
        let validUrl = null
        if(foundProduct && foundProduct.engagementUrl) {
          validUrl = filter.updateUrlWithHttp(foundProduct.engagementUrl)
        }
        if(foundProduct && validUrl) {
          this.productFound.name = foundProduct.name
          this.productFound.url = validUrl
        }
      }
    },
    validateAndSetCurrentTab() {
      if(this.isRequestingForOthers && !this.supplierOnboardRequestData.requestedFor) {
        this.userSearchIsInvalid = true
        this.$validator.validateAll()
        window.scrollTo(0,0)
      } else if (this.isRequestingForWatchers && this.watchers.length === 0) {
          this.isWatchersEmpty = true
          this.$validator.validateAll()
          window.scrollTo(0,0)
      } else if (this.supplierOnboardRequestData.companyBuyingServices.length === 0 && this.supplierOnboardRequestData.companyBuyingServiceCheckBox.length === 0){
        this.isCompanyBuyingServicesEmpty = true
        this.$validator.validateAll()
        window.scrollTo(0,0)
      } else{
        this.$validator.validateAll().then((result) => {
          if (result) {
            this.setCurrentTab("SupplierInformation");
          } else {
            window.scrollTo(0,0)
          }
        })
      }
    },
    toggleRequestingForOthers() {
      this.isRequestingForOthers ? this.setIsRequestingForOthers(false) : this.setIsRequestingForOthers(true)
    },
    toggleRequestToAddWatchers() {
      this.isRequestingForWatchers ? (this.setIsRequestingForWatchers(false), this.setWatchers([])) : this.setIsRequestingForWatchers(true)
    },
    toggleOthersSelected(){
      this.isCompanyBuyingServicesOthersSelected ? this.setIsCompanyBuyingServicesOthersSelected(false) : this.setIsCompanyBuyingServicesOthersSelected(true)
    },
    setRequestedForUserName() {
      let user = {userName: `${this.supplierOnboardRequestData.requestedFor}`,fullName: `${this.supplierOnboardRequestData.requestedForFullName}`}
      this.requestedForUser = JSON.stringify([user])
    },
    clearRequestedForUser() {
      this.updateCompanyBuyingServices(this.currentUser);
      this.fetchAdskContactInfo(this.supplierOnboardRequestData.requestedBy).then(() => {
        this.setRequestedFor({requestedFor: '', requestedForFullName: ''})
        this.requestedForUser = null
      })
    }
  },

}

</script>

<style scoped>
.preview-notification {
  margin-left: 0
}

.select-style >>> .ui.selection.dropdown .menu  {
  max-height: 150px;
}

.custom-input {
  margin-right: -5px;
  padding-top: 5px;
  padding-bottom: 5px;
}
.oneIntakeCheckBoxCountries{
  margin-right:4px;
}
</style>

<style scoped src="@/assets/stylesheets/vueSearchSelectOverrides.css">

</style>